import { FC, useState } from 'react';
import { z } from 'zod';

import { GolfCourse } from '@/api/golfCourse/golfCourse.types';
import { parThreeCountSelectOptions } from '@/api/parThree.types.ts';
import golfCourseIcon from '@/assets/golfCourseIcon.png';
import { ComboboxOption } from '@/components/controls/CustomDropdown';
import { EntityFormFieldSpecification } from '@/components/controls/CustomEntityFormField';
import CustomEntityFormModal from '@/components/controls/CustomEntityFormModal';
import CustomEntityTable from '@/components/controls/CustomEntityTable';
import Pagination from '@/components/layout/Pagination.tsx';
import CustomConfirmToast from '@/components/toast/CustomConfirmToast';
import { GolfCourseSchema } from '@/components/zod/types';
import { useGolfCourses } from '@/hooks/useGolfCourses';
import countries from '@/lib/countries';

type GolfCourseFormData = z.infer<typeof GolfCourseSchema>;

const countrySelectOptions: ComboboxOption[] = countries.map((country) => ({
  label: country.label,
  value: country.code,
}));

const golfCourseFields: EntityFormFieldSpecification[] = [
  {
    name: 'name',
    label: 'Course Name',
    fieldType: 'input',
    type: 'text',
    placeholder: 'Enter course name',
    validation: {
      required: true,
    },
  },
  {
    name: 'country',
    label: 'Country',
    fieldType: 'select',
    selectOptions: countrySelectOptions,
    type: 'text',
    placeholder: 'Select country',
    validation: {
      required: true,
    },
  },
  {
    name: 'parThree',
    label: 'Par-3 Count',
    fieldType: 'select',
    selectOptions: parThreeCountSelectOptions,
    searchable: false,
    type: 'number',
    placeholder: 'Select par-3 count',
    validation: {
      required: true,
    },
  },
];

const golfCourseColumns: {
  key: keyof GolfCourse;
  label: string;
  sortable?: boolean;
  width?: number;
}[] = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    width: 40,
  },
  {
    key: 'country',
    label: 'Country',
    sortable: true,
    width: 30,
  },
  {
    key: 'parThree',
    label: 'Par-3 Count',
    sortable: true,
    width: 30,
  },
];

const GolfCourseDashboard: FC = () => {
  const {
    isLoading,
    isFetchingAll,
    golfCoursesCount,
    golfCourses,
    pageSize,
    currentPage,
    setCurrentPage,
    selectedCourse,
    setSelectedCourse,
    deleteCourse,
    updateCourse,
    createCourse,
  } = useGolfCourses();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleAdd = (): void => {
    setShowCreateModal(true);
  };

  const handleView = (golfCourse: GolfCourse): void => {
    setSelectedCourse(golfCourse);
    setShowViewModal(true);
  };

  const handleDelete = (golfCourse: GolfCourse): void => {
    setSelectedCourse(golfCourse);
    setShowDeleteModal(true);
  };

  const cancelDelete = (): void => {
    setShowDeleteModal(false);
    setSelectedCourse(null);
  };

  const confirmDelete = (): void => {
    if (selectedCourse) {
      deleteCourse(selectedCourse);
    }
    setShowDeleteModal(false);
    setSelectedCourse(null);
  };

  const handleEditFormCancel = (): void => {
    setSelectedCourse(null);
  };

  const handleEditFormSubmit = (data: GolfCourseFormData): void => {
    if (data.country && data.name)
      updateCourse(selectedCourse.id, {
        country: data.country,
        name: data.name,
        parThree: data.parThree,
      });
  };

  const handleCreateFormSubmit = (data: GolfCourseFormData): void => {
    if (data.country && data.name)
      createCourse({
        country: data.country,
        name: data.name,
        parThree: data.parThree,
      });
    setShowCreateModal(false);
  };

  return (
    <>
      <CustomEntityTable
        title="Golf Courses"
        columns={golfCourseColumns}
        data={golfCourses}
        totalData={golfCoursesCount}
        iconName={golfCourseIcon}
        isLoading={isFetchingAll}
        onView={handleView}
        onDelete={handleDelete}
        onAdd={handleAdd}
        addButtonText="+ Add Golf Course"
      />

      <CustomEntityFormModal
        isOpen={showViewModal}
        isLoading={isLoading}
        onClose={() => setShowViewModal(false)}
        schema={GolfCourseSchema}
        entityData={selectedCourse as GolfCourseFormData}
        initialMode="view"
        fields={golfCourseFields}
        onSubmit={handleEditFormSubmit}
        onCancel={handleEditFormCancel}
        title="Golf Course Details"
      />

      <CustomEntityFormModal
        isOpen={showCreateModal}
        isLoading={isLoading}
        onClose={() => setShowCreateModal(false)}
        schema={GolfCourseSchema}
        initialMode="create"
        fields={golfCourseFields}
        onSubmit={handleCreateFormSubmit}
        onCancel={() => setShowCreateModal(false)}
        title="Add Golf Course"
      />

      <CustomConfirmToast
        header="Confirm Delete"
        show={showDeleteModal}
        message={`Are you sure you want to delete "${selectedCourse?.name}"?`}
        onAccept={confirmDelete}
        onDecline={cancelDelete}
      />

      <Pagination
        totalItems={golfCoursesCount}
        pageSize={pageSize}
        currentPage={currentPage}
        goToPage={setCurrentPage}
      />
    </>
  );
};

export default GolfCourseDashboard;
